import { FC, ChangeEvent } from 'react';
import cx from 'classnames/bind';
import { useMediaQuery } from 'react-responsive';
import type { Swiper as SwiperType } from 'swiper/types';

import { Question } from '@/components/Question';
import { MultipleQuestion } from '@/components/MultipleQuestion';
import { Button } from '@/components/Button';
import type { IBox } from '@/types/pack';
import type { Characteristic } from '@/types/characteristic';
import { getNumberPointsPerGame } from '@/utils/getNumberPointsPerGame';

import styles from './Form.module.scss';

interface FormProps {
  configId: string;
  userToken: string;
  idPack: number;
  totalPoints: number;
  setIsNotification: (value: boolean) => void;
  currentSlideIndex: any;
  setCurrentSlideIndex: any;
  setModal: (value: string) => void;
  pack: IBox;
  setCorrectAnswers: (value: string[]) => void;
  idsSelectedCheckboxes: any;
  setIdsSelectedCheckboxes: (value: any) => void;
  cursor: string;
  setCursor: (value: string) => void;
  points: number;
  isViewModal?: boolean;
  isAllPointsReceived: boolean;
  firstPlay: boolean;
}

const cn = cx.bind(styles);

const simpleHash = (configId: string) => (
  configId
    ?.split('')
    // @ts-ignore
    // eslint-disable-next-line no-bitwise
    ?.reduce((acc, char) => (acc << 5) - acc + char.charCodeAt(0), '90367125123')
    // @ts-ignore
    ?.toString(16)
);

const Form: FC<FormProps> = ({
  configId,
  userToken,
  idPack,
  totalPoints,
  setIsNotification,
  currentSlideIndex,
  setCurrentSlideIndex,
  setModal,
  pack,
  setCorrectAnswers,
  idsSelectedCheckboxes,
  setIdsSelectedCheckboxes,
  cursor,
  setCursor,
  points,
  isViewModal,
  isAllPointsReceived,
  firstPlay,
}) => {
  const isTablet = useMediaQuery({ query: '(min-width: 786px)' });

  const sendAnswer = () => {
    const isCorrectFormat =
      pack?.pack.formats[currentSlideIndex.formats].isRight;

    const isCorrectBag = pack?.pack.bag?.[currentSlideIndex.bag - 1]?.isRight;

    const isCorrectFilter =
      pack?.pack.filter?.[currentSlideIndex.filter - 1]?.isRight;

    const correctCapsulesIds = pack?.pack.capsules
      .filter(({ isRight }) => isRight)
      .map(({ id }) => id);

    const sortedIdsSelectedCapsules = idsSelectedCheckboxes.capsules.sort();

    const sortedCorrectCapsulesIds = correctCapsulesIds.sort();

    const isCorrectCapsules = !!(
      sortedIdsSelectedCapsules.length === sortedCorrectCapsulesIds.length &&
      sortedIdsSelectedCapsules.every(
        (element: number, index: number) =>
          element === sortedCorrectCapsulesIds[index],
      )
    );

    const correctPeculiaritiesIds = pack?.pack.peculiarities
      .filter(({ isRight }) => isRight)
      .map(({ id }) => id);

    const sortedIdsSelectedPeculiarities =
      idsSelectedCheckboxes.peculiarities.sort();

    const sortedCorrectPeculiaritiesIds = correctPeculiaritiesIds.sort();

    const isCorrectPeculiarities = !!(
      sortedIdsSelectedPeculiarities.length ===
        sortedCorrectPeculiaritiesIds.length &&
      sortedIdsSelectedPeculiarities.every(
        (element: number, index: number) =>
          element === sortedCorrectPeculiaritiesIds[index],
      )
    );

    const correctListCapsules = pack?.pack.capsules
      .filter(({ id }) => idsSelectedCheckboxes.capsules.includes(id))
      .filter(({ isRight }) => isRight)
      .map(({ text }) => text);

    const correctListPeculiarities = pack?.pack.peculiarities
      .filter(({ id }) => idsSelectedCheckboxes.peculiarities.includes(id))
      .filter(({ isRight }) => isRight)
      .map(({ text }) => text);

    const correctListAnswers = [
      ...(isCorrectFormat
        ? [pack?.pack.formats[currentSlideIndex.formats].text]
        : []),
      ...(isCorrectBag
        ? [pack?.pack.bag?.[currentSlideIndex.bag - 1].text]
        : []),
      ...(isCorrectFilter
        ? [pack?.pack.filter?.[currentSlideIndex.filter - 1].text]
        : []),
      ...(correctListCapsules || []),
      ...(correctListPeculiarities || []),
    ];

    setCorrectAnswers(correctListAnswers);

    const isCorrectAnswer =
      isCorrectFormat &&
      isCorrectBag &&
      isCorrectFilter &&
      isCorrectCapsules &&
      isCorrectPeculiarities;

    setIsNotification(!!isCorrectAnswer);

    if (isCorrectAnswer) {
      setModal('correctOrder');
    } else {
      setModal('incorrectOrder');
    }

    const numberPointsPerGame = getNumberPointsPerGame(totalPoints);
    // Отправлять запрос только для игр за баллы
    if (pack.achievement === null) {
      fetch(
        `https://admin.znatok.jti-games.dev.familyagency.ru/api/orders/${idPack}/save-player`,
        {
          method: 'POST',
          headers: {
            UserToken: userToken,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            score: isCorrectAnswer ? numberPointsPerGame : 0,
            achievement: isCorrectAnswer,
          }),
        },
      )
        .then((response) => response.json())
        .then(() => {
          fetch(`https://admin.znatok.jti-games.dev.familyagency.ru/api/orders`, {
            headers: {
              UserToken: userToken,
            },
          })
            .then((response) => response.json())
            .then(({ data }) => {
              const thirdList = data?.[2]?.orders as IBox[];
              const isThirdGameCompleted = thirdList?.every(
                ({ achievement }) => achievement !== null,
              );
              // TODO:: отправить запрос со всеми полученными баллами в последней игре
              if (isViewModal && isThirdGameCompleted) {
                window.jticonnexus.setScore(
                  (points + (isCorrectAnswer ? numberPointsPerGame : 0)) /
                    totalPoints, { meta1: 100, id: simpleHash(configId) }, { addToRanking: true },
                );
                fetch(`https://admin.znatok.jti-games.dev.familyagency.ru/api/modal/update`, {
                  method: 'POST',
                  headers: {
                    UserToken: userToken,
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    isViewModal: false,
                  }),
                });
              }
            });
        });
    }

    // Отправка запросов без баллов
    if (isAllPointsReceived && pack.achievement_without_score === null) {
      fetch(
        `https://admin.znatok.jti-games.dev.familyagency.ru/api/orders/${idPack}/save-player`,
        {
          method: 'POST',
          headers: {
            UserToken: userToken,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            achievementWithoutScore: isCorrectAnswer,
          }),
        },
      ).then((response) => response.json());
    }
  };

  const getCurrentSlideIndex = (
    e: SwiperType,
    characteristic: Characteristic,
  ) => {
    if (setCurrentSlideIndex) {
      setCurrentSlideIndex((prev: any) => ({
        ...prev,
        [characteristic]: e.realIndex,
      }));
    }
  };

  const changeCheckboxValue = (
    e: ChangeEvent<HTMLInputElement>,
    id: number,
    characteristic: string,
  ) => {
    if (e.target.checked) {
      setIdsSelectedCheckboxes((prev: any) => ({
        ...prev,
        [characteristic]: [...(prev[characteristic] || []), id],
      }));
    } else {
      setIdsSelectedCheckboxes((prev: any) => ({
        ...prev,
        [characteristic]: prev[characteristic].filter(
          (idChar: number) => id !== idChar,
        ),
      }));
    }
  };

  const resetCheckboxes = (characteristic: 'capsules' | 'peculiarities') => {
    setIdsSelectedCheckboxes((prev: any) => ({
      ...prev,
      [characteristic]: [],
    }));
  };

  const isDisabled = (id: number) =>
    idsSelectedCheckboxes.capsules.length === 2 &&
    !idsSelectedCheckboxes.capsules.includes(id);

  // Доступность кнопки "Выдать заказ" после выбора мешки и фильтра
  const isAvailableButton = currentSlideIndex.bag && currentSlideIndex.filter;

  const color1 = document.querySelector('[data-characteristic="color1"]')!;

  const color2 = document.querySelector('[data-characteristic="color2"]')!;

  const capsule1 = document.querySelector('[data-characteristic="capsule1"]')!;

  const capsule2 = document.querySelector('[data-characteristic="capsule2"]')!;

  const resetCapsules = () => {
    if (capsule1) {
      capsule1.setAttribute('display', 'none');
    }

    if (capsule2) {
      capsule2.setAttribute('display', 'none');
    }

    if (color1) {
      color1.removeAttribute('fill');
    }

    if (color2) {
      color2.removeAttribute('fill');
    }
  };

  return (
    <form className={cn('form')}>
      <div className={cn('list')}>
        <Question
          title="Формат"
          answers={pack.pack.formats}
          isDefaultValue
          getCurrentSlideIndex={(e: SwiperType) =>
            getCurrentSlideIndex(e, 'formats')
          }
          cursor={cursor}
          setCursor={setCursor}
          firstPlay={firstPlay}
        />
        <Question
          title="Мешка табака"
          answers={pack.pack.bag}
          getCurrentSlideIndex={(e: SwiperType) =>
            getCurrentSlideIndex(e, 'bag')
          }
        />
        <Question
          title="Фильтр"
          answers={pack.pack.filter}
          getCurrentSlideIndex={(e: SwiperType) =>
            getCurrentSlideIndex(e, 'filter')
          }
        />
        <MultipleQuestion
          title="Наличие капсулы"
          answers={pack.pack.capsules}
          handleClick={resetCapsules}
          changeCheckboxValue={(e: ChangeEvent<HTMLInputElement>, id: number) =>
            changeCheckboxValue(e, id, 'capsules')
          }
          isDisabled={(id: number) => isDisabled(id)}
          resetCheckboxes={() => resetCheckboxes('capsules')}
          cursor={cursor}
          setCursor={setCursor}
          firstPlay={firstPlay}
        />
        <MultipleQuestion
          title="Наличие особенностей"
          answers={pack.pack.peculiarities}
          changeCheckboxValue={(e: ChangeEvent<HTMLInputElement>, id: number) =>
            changeCheckboxValue(e, id, 'peculiarities')
          }
          resetCheckboxes={() => resetCheckboxes('peculiarities')}
        />
      </div>
      <div className={cn('wrapper')}>
        {isTablet && (
          <div className={cn('description')}>
            Верно настройте характеристики в каждом пункте для сигареты
            указанной пачки
          </div>
        )}
        <Button
          className={cn('button')}
          handleClick={sendAnswer}
          disabled={!isAvailableButton}
        >
          Выдать заказ
        </Button>
      </div>
    </form>
  );
};

export default Form;
